<template>
  <div class="register-page min-vh-100 d-flex align-items-center">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-8 col-lg-6 col-xl-5">
          <div class="card shadow-lg border-0 rounded-3">
            <div class="card-body p-5">
              <!-- Logo and Title -->
              <div class="text-center mb-4">
                <div class="mb-3">
                  <i class="bi bi-heart-pulse" style="font-size: 3rem; color: var(--primary-color)"></i>
                </div>
                <h2 class="fw-bold text-dark mb-2">Join FemWellness</h2>
                <p class="text-muted">Create your account and start your wellness journey</p>
              </div>

              <!-- Alert Messages -->
              <div
                v-if="alertMessage"
                :class="alertClass"
                class="alert alert-dismissible fade show"
                role="alert"
              >
                {{ alertMessage }}
                <button
                  type="button"
                  class="btn-close"
                  @click="clearAlert"
                  aria-label="Close"
                ></button>
              </div>

              <!-- Registration Form -->
              <form @submit.prevent="handleRegister" novalidate>
                <!-- First Name Field -->
                <div class="mb-3">
                  <label for="firstName" class="form-label fw-semibold">
                    First Name <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="bi bi-person"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.firstName, 'is-valid': firstName && !errors.firstName }"
                      id="firstName"
                      v-model="firstName"
                      @blur="validateFirstName"
                      @input="clearFieldError('firstName')"
                      placeholder="Enter your first name"
                      required
                    />
                    <div class="invalid-feedback">{{ errors.firstName }}</div>
                  </div>
                </div>

                <!-- Last Name Field -->
                <div class="mb-3">
                  <label for="lastName" class="form-label fw-semibold">
                    Last Name <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="bi bi-person"></i>
                    </span>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.lastName, 'is-valid': lastName && !errors.lastName }"
                      id="lastName"
                      v-model="lastName"
                      @blur="validateLastName"
                      @input="clearFieldError('lastName')"
                      placeholder="Enter your last name"
                      required
                    />
                    <div class="invalid-feedback">{{ errors.lastName }}</div>
                  </div>
                </div>

                <!-- Email Field -->
                <div class="mb-3">
                  <label for="email" class="form-label fw-semibold">
                    Email Address <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="bi bi-envelope"></i>
                    </span>
                    <input
                      type="email"
                      class="form-control"
                      :class="{ 'is-invalid': errors.email, 'is-valid': email && !errors.email }"
                      id="email"
                      v-model="email"
                      @blur="validateEmail"
                      @input="clearFieldError('email')"
                      placeholder="Enter your email"
                      required
                    />
                    <div class="invalid-feedback">{{ errors.email }}</div>
                  </div>
                </div>

                <!-- Password Field -->
                <div class="mb-3">
                  <label for="password" class="form-label fw-semibold">
                    Password <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="bi bi-lock"></i>
                    </span>
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      class="form-control"
                      :class="{ 'is-invalid': errors.password, 'is-valid': password && !errors.password }"
                      id="password"
                      v-model="password"
                      @blur="validatePassword"
                      @input="clearFieldError('password')"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      @click="togglePasswordVisibility"
                    >
                      <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                    </button>
                    <div class="invalid-feedback">{{ errors.password }}</div>
                  </div>
                  <!-- Password Requirements -->
                  <div class="mt-2">
                    <small class="text-muted">
                      Password must contain at least 8 characters, including uppercase, lowercase, number, and special character.
                    </small>
                  </div>
                </div>

                <!-- Confirm Password Field -->
                <div class="mb-3">
                  <label for="confirmPassword" class="form-label fw-semibold">
                    Confirm Password <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="bi bi-lock-fill"></i>
                    </span>
                    <input
                      :type="showConfirmPassword ? 'text' : 'password'"
                      class="form-control"
                      :class="{ 'is-invalid': errors.confirmPassword, 'is-valid': confirmPassword && !errors.confirmPassword }"
                      id="confirmPassword"
                      v-model="confirmPassword"
                      @blur="validateConfirmPassword"
                      @input="clearFieldError('confirmPassword')"
                      placeholder="Confirm your password"
                      required
                    />
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      @click="toggleConfirmPasswordVisibility"
                    >
                      <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                    </button>
                    <div class="invalid-feedback">{{ errors.confirmPassword }}</div>
                  </div>
                </div>

                <!-- Account Type Selection -->
                <div class="mb-3">
                  <label class="form-label fw-semibold">
                    Account Type <span class="text-danger">*</span>
                  </label>
                  <div class="row g-3">
                    <div class="col-4">
                      <div class="form-check form-check-card">
                        <input
                          class="form-check-input"
                          type="radio"
                          name="userRole"
                          id="roleUser"
                          value="user"
                          v-model="selectedRole"
                        />
                        <label class="form-check-label w-100 p-3 border rounded text-center" for="roleUser">
                          <i class="bi bi-person-fill d-block mb-2 fs-4 text-primary"></i>
                          <strong>User</strong>
                          <small class="d-block text-muted">Access wellness features</small>
                        </label>
                      </div>
                    </div>
                    <div class="col-4">
                      <div class="form-check form-check-card">
                        <input
                          class="form-check-input"
                          type="radio"
                          name="userRole"
                          id="roleDoctor"
                          value="doctor"
                          v-model="selectedRole"
                        />
                        <label class="form-check-label w-100 p-3 border rounded text-center" for="roleDoctor">
                          <i class="bi bi-person-badge-fill d-block mb-2 fs-4 text-success"></i>
                          <strong>Doctor</strong>
                          <small class="d-block text-muted">Medical professional</small>
                        </label>
                      </div>
                    </div>
                    <div class="col-4">
                      <div class="form-check form-check-card">
                        <input
                          class="form-check-input"
                          type="radio"
                          name="userRole"
                          id="roleAdmin"
                          value="admin"
                          v-model="selectedRole"
                        />
                        <label class="form-check-label w-100 p-3 border rounded text-center" for="roleAdmin">
                          <i class="bi bi-shield-fill d-block mb-2 fs-4 text-warning"></i>
                          <strong>Admin</strong>
                          <small class="d-block text-muted">System administrator</small>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div v-if="errors.role" class="invalid-feedback d-block">{{ errors.role }}</div>
                </div>

                <!-- Doctor Verification Notice -->
                <div v-if="selectedRole === 'doctor'" class="alert alert-info">
                  <i class="bi bi-info-circle me-2"></i>
                  <strong>Doctor Account:</strong> Your account will require verification before you can access doctor features. Please ensure you have your medical license ready for upload.
                </div>

                <!-- Terms and Conditions -->
                <div class="mb-3">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      :class="{ 'is-invalid': errors.terms }"
                      type="checkbox"
                      id="terms"
                      v-model="acceptTerms"
                      @change="validateTerms"
                      required
                    />
                    <label class="form-check-label" for="terms">
                      I agree to the <a href="#" class="text-primary">Terms of Service</a> and
                      <a href="#" class="text-primary">Privacy Policy</a> <span class="text-danger">*</span>
                    </label>
                    <div class="invalid-feedback">{{ errors.terms }}</div>
                  </div>
                </div>

                <!-- Register Button -->
                <div class="d-grid">
                  <button
                    type="submit"
                    class="btn btn-primary btn-lg fw-semibold"
                    :disabled="isSubmitting"
                  >
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                    {{ isSubmitting ? 'Creating Account...' : 'Create Account' }}
                  </button>
                </div>
              </form>

              <!-- Divider -->
              <div class="text-center my-4">
                <span class="text-muted">Or sign up with</span>
              </div>

              <!-- Social Login Options -->
              <div>
                <button type="button" class="btn btn-outline-dark w-100" @click="signUpWithGoogle">
                  <i class="bi bi-google me-2"></i>Google
                </button>
              </div>

              <!-- Login Link -->
              <div class="text-center mt-4">
                <span class="text-muted">Already have an account? </span>
                <router-link to="/login" class="text-primary text-decoration-none fw-semibold">
                  Sign in here
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import { USER_ROLES } from '@/utils/roles'

export default {
  name: 'Register',
  setup() {
    const { register, loginWithGoogle } = useFirebaseAuth()
    return {
      firebaseRegister: register,
      firebaseLoginWithGoogle: loginWithGoogle
    }
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      selectedRole: USER_ROLES.USER,
      acceptTerms: false,
      showPassword: false,
      showConfirmPassword: false,
      isSubmitting: false,
      alertMessage: '',
      alertClass: '',
      errors: {}
    }
  },
  methods: {
    // Validation Methods
    validateFirstName() {
      if (!this.firstName.trim()) {
        this.errors.firstName = 'First name is required'
        return false
      }
      if (this.firstName.trim().length < 2) {
        this.errors.firstName = 'First name must be at least 2 characters'
        return false
      }
      if (!/^[a-zA-Z\s]+$/.test(this.firstName.trim())) {
        this.errors.firstName = 'First name can only contain letters and spaces'
        return false
      }
      delete this.errors.firstName
      return true
    },

    validateLastName() {
      if (!this.lastName.trim()) {
        this.errors.lastName = 'Last name is required'
        return false
      }
      if (this.lastName.trim().length < 2) {
        this.errors.lastName = 'Last name must be at least 2 characters'
        return false
      }
      if (!/^[a-zA-Z\s]+$/.test(this.lastName.trim())) {
        this.errors.lastName = 'Last name can only contain letters and spaces'
        return false
      }
      delete this.errors.lastName
      return true
    },

    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!this.email) {
        this.errors.email = 'Email is required'
        return false
      }
      if (!emailRegex.test(this.email)) {
        this.errors.email = 'Please enter a valid email address'
        return false
      }
      delete this.errors.email
      return true
    },

    validatePassword() {
      if (!this.password) {
        this.errors.password = 'Password is required'
        return false
      }
      if (this.password.length < 8) {
        this.errors.password = 'Password must be at least 8 characters long'
        return false
      }
      if (!/(?=.*[a-z])/.test(this.password)) {
        this.errors.password = 'Password must contain at least one lowercase letter'
        return false
      }
      if (!/(?=.*[A-Z])/.test(this.password)) {
        this.errors.password = 'Password must contain at least one uppercase letter'
        return false
      }
      if (!/(?=.*\d)/.test(this.password)) {
        this.errors.password = 'Password must contain at least one number'
        return false
      }
      if (!/(?=.*[@$!%*?&])/.test(this.password)) {
        this.errors.password = 'Password must contain at least one special character (@$!%*?&)'
        return false
      }
      delete this.errors.password
      // Revalidate confirm password if it exists
      if (this.confirmPassword) {
        this.validateConfirmPassword()
      }
      return true
    },

    validateConfirmPassword() {
      if (!this.confirmPassword) {
        this.errors.confirmPassword = 'Please confirm your password'
        return false
      }
      if (this.password !== this.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match'
        return false
      }
      delete this.errors.confirmPassword
      return true
    },

    validateTerms() {
      if (!this.acceptTerms) {
        this.errors.terms = 'You must accept the terms and conditions'
        return false
      }
      delete this.errors.terms
      return true
    },

    validateRole() {
      if (!this.selectedRole) {
        this.errors.role = 'Please select an account type'
        return false
      }
      delete this.errors.role
      return true
    },

    // Utility Methods
    clearFieldError(field) {
      if (this.errors[field]) {
        delete this.errors[field]
      }
    },

    clearAlert() {
      this.alertMessage = ''
      this.alertClass = ''
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword
    },

    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = !this.showConfirmPassword
    },

    // Form Submission
    async handleRegister() {
      // Validate all fields
      const isFirstNameValid = this.validateFirstName()
      const isLastNameValid = this.validateLastName()
      const isEmailValid = this.validateEmail()
      const isPasswordValid = this.validatePassword()
      const isConfirmPasswordValid = this.validateConfirmPassword()
      const isTermsValid = this.validateTerms()

      const isRoleValid = this.validateRole()

      if (!isFirstNameValid || !isLastNameValid || !isEmailValid || !isPasswordValid || !isConfirmPasswordValid || !isRoleValid || !isTermsValid) {
        this.showAlert('Please fix the errors below', 'alert-danger')
        return
      }

      this.isSubmitting = true

      try {
        // Use Firebase to register the user with role
        const displayName = `${this.firstName.trim()} ${this.lastName.trim()}`
        const result = await this.firebaseRegister(this.email, this.password, displayName, this.selectedRole)

        if (result.success) {
          // Success
          this.showAlert('Account created successfully! You can now sign in with your credentials.', 'alert-success')

          // Clear form
          this.resetForm()

          // Redirect to home after delay
          setTimeout(() => {
            this.$router.push('/')
          }, 3000)
        } else {
          // Handle specific Firebase errors
          let errorMessage = 'Registration failed. Please try again.'
          
          if (result.error) {
            if (result.error.includes('email-already-in-use')) {
              errorMessage = 'An account with this email already exists. Please use a different email or try logging in.'
            } else if (result.error.includes('weak-password')) {
              errorMessage = 'Password is too weak. Please use a stronger password.'
            } else if (result.error.includes('invalid-email')) {
              errorMessage = 'Invalid email address format.'
            } else {
              errorMessage = result.error
            }
          }
          
          this.showAlert(errorMessage, 'alert-danger')
        }

      } catch (error) {
        this.showAlert('Registration failed. Please try again.', 'alert-danger')
      } finally {
        this.isSubmitting = false
      }
    },

    resetForm() {
      this.firstName = ''
      this.lastName = ''
      this.email = ''
      this.password = ''
      this.confirmPassword = ''
      this.selectedRole = USER_ROLES.USER
      this.acceptTerms = false
      this.errors = {}
    },

    showAlert(message, type) {
      this.alertMessage = message
      this.alertClass = type
    },

    // Social Registration Methods
    async signUpWithGoogle() {
      this.clearAlert()
      this.isSubmitting = true

      try {
        const result = await this.firebaseLoginWithGoogle()

        if (result.success) {
          this.showAlert('Google sign up successful! Redirecting...', 'alert-success')
          setTimeout(() => {
            this.$router.push(this.$route.query.redirect || '/')
          }, 1500)
        } else {
          let errorMessage = 'Google sign up failed. Please try again.'
          if (result.error) {
            errorMessage = result.error
          }
          this.showAlert(errorMessage, 'alert-danger')
        }
      } catch (error) {
        this.showAlert('Google sign up failed. Please try again.', 'alert-danger')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.register-page {
  background: linear-gradient(135deg, #fef7f7 0%, #f3e5f5 100%);
}

.card {
  border-radius: 15px;
  backdrop-filter: blur(10px);
}

.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
  color: #6c757d;
}

.form-control {
  border-left: none;
  padding-left: 0;
}

.form-control:focus {
  box-shadow: 0 0 0 0.2rem rgba(233, 30, 99, 0.25);
  border-color: var(--primary-color);
}

.form-control:focus + .btn {
  border-color: var(--primary-color);
}

.btn-primary:hover {
  transform: translateY(-1px);
}

/* Role Selection Cards */
.form-check-card {
  position: relative;
}

.form-check-card .form-check-input {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 2;
}

.form-check-card .form-check-label {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #dee2e6 !important;
}

.form-check-card .form-check-label:hover {
  border-color: var(--primary-color) !important;
  background-color: rgba(233, 30, 99, 0.05);
}

.form-check-card .form-check-input:checked + .form-check-label {
  border-color: var(--primary-color) !important;
  background-color: rgba(233, 30, 99, 0.1);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

@media (max-width: 768px) {
  .card-body {
    padding: 2rem !important;
  }

  .row.g-2 .col {
    margin-bottom: 0.5rem;
  }
}
</style>
